export interface SessionProfile {
  id: string
  userName: string
  email: string
  fullName: string
  isActive: boolean
  profileImageUrl?: string | null
}

export interface SessionPayload {
  accessToken: string
  refreshToken: string
  user: SessionProfile
  roles: string[]
  permissions: string[]
  // Đợt 5 M67: mật khẩu tạm do quản trị đặt, phải đổi trước khi dùng ứng dụng.
  mustChangePassword?: boolean
}

export interface SessionSnapshot {
  accessToken: string | null
  refreshToken: string | null
  profile: SessionProfile | null
  roles: string[]
  permissions: string[]
  mustChangePassword: boolean
}

export interface RefreshSessionGuard {
  revision: number
  refreshToken: string
}

const keys = {
  accessToken: 'qone.access',
  refreshToken: 'qone.refresh',
  profile: 'qone.profile',
  roles: 'qone.roles',
  permissions: 'qone.permissions',
  mustChangePassword: 'qone.mustChangePassword'
} as const

const sessionKeys = Object.values(keys)
const commitKey = 'qone.session'
const listeners = new Set<(snapshot: SessionSnapshot) => void>()
let revision = 0
type CommittedSession = { generation: string; snapshot: SessionSnapshot }
const emptySession = (): SessionSnapshot => ({ accessToken: null, refreshToken: null, profile: null, roles: [], permissions: [], mustChangePassword: false })
let observedCommit = localStorage.getItem(commitKey)
let generation = 'legacy'
let snapshot = observedCommit === null ? readLegacySession() : readCommitted(observedCommit)?.snapshot ?? emptySession()
generation = readCommitted(observedCommit)?.generation ?? generation
// A preceding-version logout can happen while every new-client tab is closed.
// Refresh never removes the access mirror; its absence at bootstrap is a logout.
if (observedCommit !== null && localStorage.getItem(keys.accessToken) === null) snapshot = emptySession()

function readJson<T>(key: string, fallback: T): T {
  const value = localStorage.getItem(key)
  if (!value) return fallback
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

function canonicalScope(values: string[]): string[] { return [...new Set(values)].sort() }

function readLegacySession(): SessionSnapshot {
  return {
    accessToken: localStorage.getItem(keys.accessToken),
    refreshToken: localStorage.getItem(keys.refreshToken),
    profile: readJson<SessionProfile | null>(keys.profile, null),
    roles: canonicalScope(readJson<string[]>(keys.roles, [])),
    permissions: canonicalScope(readJson<string[]>(keys.permissions, [])),
    mustChangePassword: localStorage.getItem(keys.mustChangePassword) === 'true'
  }
}

function readCommitted(raw: string | null): CommittedSession | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as CommittedSession
    const session = value.snapshot
    if (typeof value.generation !== 'string' || !session || !Array.isArray(session.roles)
      || !session.roles.every(role => typeof role === 'string') || !Array.isArray(session.permissions)
      || !session.permissions.every(permission => typeof permission === 'string')
      || typeof session.mustChangePassword !== 'boolean') return null
    if (session.accessToken === null && session.refreshToken === null && session.profile === null)
      return { generation: value.generation, snapshot: emptySession() }
    if (typeof session.accessToken !== 'string' || typeof session.refreshToken !== 'string'
      || !session.profile || typeof session.profile.id !== 'string' || typeof session.profile.isActive !== 'boolean') return null
    return { ...value, snapshot: { ...session, roles: canonicalScope(session.roles), permissions: canonicalScope(session.permissions) } }
  } catch { return null }
}

function authorizationScope(value: SessionSnapshot) {
  return JSON.stringify([value.profile?.id, value.profile?.isActive, value.mustChangePassword,
    [...value.roles].sort(), [...value.permissions].sort()])
}

// The final atomic envelope is the commit point. Compatibility-key storage events may arrive
// between writes; they must never publish a mixed actor/token/permission snapshot.
function synchronize() {
  const raw = localStorage.getItem(commitKey)
  if (raw === observedCommit) return
  const next = readCommitted(raw)
  const nextSnapshot = next?.snapshot ?? emptySession()
  if (!next || next.generation !== generation || authorizationScope(nextSnapshot) !== authorizationScope(snapshot)) revision++
  observedCommit = raw
  generation = next?.generation ?? 'cleared'
  snapshot = nextSnapshot
  publish(snapshot)
}

export function readSession(): SessionSnapshot {
  synchronize()
  // Initial sessions written by the preceding client version remain readable. Once an
  // external change or an atomic session has been observed, never resume this fallback.
  if (observedCommit === null && generation === 'legacy') snapshot = readLegacySession()
  return snapshot
}

function updateStorage(values: ReadonlyMap<string, string | null>, nextSnapshot: SessionSnapshot, nextGeneration = generation) {
  const previous = new Map(sessionKeys.map(key => [key, localStorage.getItem(key)]))
  const previousCommit = localStorage.getItem(commitKey)
  // Serialize caller-owned coherent state before touching any shared compatibility key.
  const committed = JSON.stringify({ generation: nextGeneration, snapshot: nextSnapshot })
  try {
    for (const [key, value] of values) {
      if (value === null) localStorage.removeItem(key)
      else localStorage.setItem(key, value)
    }
    if (localStorage.getItem(commitKey) !== previousCommit) throw new Error('Session changed during storage update.')
    localStorage.setItem(commitKey, committed)
  } catch (error) {
    // setItem itself is atomic on failure. Never restore a canonical value over another tab's commit.
    if (localStorage.getItem(commitKey) === previousCommit) {
      for (const [key, value] of previous) {
        if (value === null) localStorage.removeItem(key)
        else localStorage.setItem(key, value)
      }
    }
    throw error
  }
}

function sessionValues(payload: SessionPayload) {
  return new Map<string, string | null>([
    [keys.accessToken, payload.accessToken],
    [keys.refreshToken, payload.refreshToken],
    [keys.profile, JSON.stringify(payload.user)],
    [keys.roles, JSON.stringify(payload.roles)],
    [keys.permissions, JSON.stringify(payload.permissions)],
    [keys.mustChangePassword, payload.mustChangePassword ? 'true' : null]
  ])
}

function payloadSnapshot(payload: SessionPayload): SessionSnapshot {
  return { accessToken: payload.accessToken, refreshToken: payload.refreshToken, profile: { ...payload.user },
    roles: canonicalScope(payload.roles), permissions: canonicalScope(payload.permissions), mustChangePassword: payload.mustChangePassword ?? false }
}

function publish(snapshot: SessionSnapshot) {
  for (const listener of listeners) listener(snapshot)
}

export function startSession(payload: SessionPayload) {
  synchronize()
  updateStorage(sessionValues(payload), payloadSnapshot(payload), crypto.randomUUID())
  const snapshot = readSession()
  return snapshot
}

export function captureRefreshSessionGuard(refreshToken: string): RefreshSessionGuard {
  synchronize()
  return { revision, refreshToken }
}

export function commitRefreshedSession(payload: SessionPayload, guard: RefreshSessionGuard) {
  synchronize()
  if (revision !== guard.revision || snapshot.refreshToken !== guard.refreshToken) return null
  updateStorage(sessionValues(payload), payloadSnapshot(payload))
  return readSession()
}

export function isSessionRevisionCurrent(expectedRevision: number) {
  synchronize()
  return revision === expectedRevision
}

export function currentSessionRevision() {
  synchronize()
  return revision
}

export function clearSession(guard?: RefreshSessionGuard) {
  synchronize()
  if (guard && (revision !== guard.revision || snapshot.refreshToken !== guard.refreshToken)) {
    return false
  }
  updateStorage(new Map(sessionKeys.map(key => [key, null])), emptySession(), crypto.randomUUID())
  synchronize()
  return true
}

export function updateSessionProfile(profile: SessionProfile) {
  synchronize()
  updateStorage(new Map([[keys.profile, JSON.stringify(profile)]]), { ...snapshot, profile: { ...profile } })
  synchronize()
}

export function subscribeSession(listener: (snapshot: SessionSnapshot) => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function onStorage(event: StorageEvent) {
  if (event.storageArea !== null && event.storageArea !== localStorage) return
  if (event.key !== null && event.key !== commitKey && !sessionKeys.includes(event.key as typeof sessionKeys[number])) return
  synchronize()
  if (event.key === keys.accessToken && event.newValue === null && localStorage.getItem(keys.accessToken) === null
    && snapshot.accessToken !== null) {
    // A preceding-version client removed the token. Persist an empty commit so reload cannot
    // resurrect it; the storage writer refuses to overwrite a concurrently committed new session.
    const logoutCommit = observedCommit
    try { clearSession() } catch {
      synchronize()
      if (observedCommit === logoutCommit) {
        revision++; generation = 'cleared'; snapshot = emptySession(); publish(snapshot)
      }
    }
  }
  // Older clients have no atomic envelope. External partial writes/removals cannot establish
  // a coherent authenticated scope; wait for a new committed session instead of guessing.
  if (localStorage.getItem(commitKey) === null && event.key !== commitKey) {
    revision++; generation = 'cleared'; snapshot = emptySession(); publish(snapshot)
  }
}
window.addEventListener('storage', onStorage)
if (import.meta.hot) import.meta.hot.dispose(() => window.removeEventListener('storage', onStorage))
